import { z } from 'zod';

/**
 * Appointment type from EHR.
 */
export const ehrAppointmentTypeSchema = z.object({
  code: z.string(),
  name: z.string(),
  description: z.string().optional(),
  durationMinutes: z.number().optional(),
  category: z.string().optional(), // e.g., "office-visit", "telehealth", "procedure"
});

export type EhrAppointmentType = z.infer<typeof ehrAppointmentTypeSchema>;

/**
 * Appointment status.
 */
export const appointmentStatusSchema = z.enum([
  'proposed',
  'pending',
  'booked',
  'arrived',
  'fulfilled',
  'cancelled',
  'noshow',
  'waitlist',
]);

export type AppointmentStatus = z.infer<typeof appointmentStatusSchema>;

/**
 * Appointment from EHR.
 */
export const ehrAppointmentSchema = z.object({
  ehrAppointmentId: z.string(),
  ehrPatientId: z.string(),
  ehrProviderId: z.string(),
  ehrLocationId: z.string(),
  startTime: z.string(), // ISO 8601 datetime
  endTime: z.string(), // ISO 8601 datetime
  durationMinutes: z.number(),
  status: appointmentStatusSchema,
  appointmentType: ehrAppointmentTypeSchema.optional(),
  reasonForVisit: z.string().optional(),
  notes: z.string().optional(),
  confirmationNumber: z.string().optional(),
  checkinTime: z.string().optional(), // ISO 8601 datetime
  checkoutTime: z.string().optional(), // ISO 8601 datetime
  cancellationReason: z.string().optional(),
  /**
   * On a cancelled appointment that was moved rather than called off: the EHR id of
   * the appointment it was moved to. athena reschedules by cancelling the old
   * appointment and booking a new one (`rescheduledappointmentid`). Absent for a
   * plain cancellation and for EHRs that don't report it.
   */
  rescheduledToEhrAppointmentId: z.string().optional(),
  isTelehealth: z.boolean().optional(),
  telehealthUrl: z.string().optional(),
  providerName: z.string().optional(),
  locationName: z.string().optional(),
  createdAt: z.string().optional(), // ISO 8601 datetime
  updatedAt: z.string().optional(), // ISO 8601 datetime
});

export type EhrAppointment = z.infer<typeof ehrAppointmentSchema>;

/**
 * Patient appointments response.
 */
export const ehrPatientAppointmentsSchema = z.object({
  ehrPatientId: z.string(),
  upcoming: z.array(ehrAppointmentSchema),
  past: z.array(ehrAppointmentSchema),
  fetchedAt: z.string(), // ISO 8601 datetime
});

export type EhrPatientAppointments = z.infer<typeof ehrPatientAppointmentsSchema>;

/**
 * Create appointment request.
 */
export const createAppointmentRequestSchema = z.object({
  ehrPatientId: z.string(),
  ehrProviderId: z.string(),
  ehrLocationId: z.string(),
  slotId: z.string().optional(), // If booking a specific slot
  startTime: z.string(), // ISO 8601 datetime
  durationMinutes: z.number().optional(),
  appointmentTypeCode: z.string().optional(),
  reasonForVisit: z.string().optional(),
  notes: z.string().optional(),
  isTelehealth: z.boolean().optional(),
});

export type CreateAppointmentRequest = z.infer<typeof createAppointmentRequestSchema>;

/**
 * Create appointment response.
 */
export const createAppointmentResponseSchema = z.object({
  appointment: ehrAppointmentSchema,
  confirmationNumber: z.string(),
});

export type CreateAppointmentResponse = z.infer<typeof createAppointmentResponseSchema>;

/**
 * Cancel appointment request.
 */
export const cancelAppointmentRequestSchema = z.object({
  ehrAppointmentId: z.string(),
  cancellationReason: z.string().optional(),
});

export type CancelAppointmentRequest = z.infer<typeof cancelAppointmentRequestSchema>;

/**
 * Reschedule appointment request.
 */
export const rescheduleAppointmentRequestSchema = z.object({
  ehrAppointmentId: z.string(),
  newSlotId: z.string().optional(),
  newStartTime: z.string(), // ISO 8601 datetime
  newEhrProviderId: z.string().optional(),
  newEhrLocationId: z.string().optional(),
  rescheduleReason: z.string().optional(),
});

export type RescheduleAppointmentRequest = z.infer<typeof rescheduleAppointmentRequestSchema>;

/**
 * Who a visit reason applies to: new patients, existing patients, or anyone.
 */
export const visitReasonPatientTypeSchema = z.enum(['new', 'existing', 'all']);

export type VisitReasonPatientType = z.infer<typeof visitReasonPatientTypeSchema>;

/** A provider at a location, by EHR ids — where a visit reason can be booked. */
export const ehrProviderLocationSchema = z.object({
  ehrProviderId: z.string(),
  ehrLocationId: z.string(),
});

export type EhrProviderLocation = z.infer<typeof ehrProviderLocationSchema>;

/**
 * A patient-facing reason for a visit ("Annual physical", "Sick visit") as the EHR
 * offers it for self-scheduling — distinct from the practice's internal appointment
 * types, which the EHR maps each reason onto per provider and location.
 */
export const ehrVisitReasonSchema = z.object({
  ehrReasonId: z.string(),
  name: z.string(),
  /** Patient-facing explanation of the reason. */
  description: z.string().optional(),
  patientType: visitReasonPatientTypeSchema,
  /** The earliest a slot may be booked for this reason, in hours from now. */
  minHoursAhead: z.number().optional(),
  /** The furthest ahead a slot may be booked for this reason, in days. */
  maxDaysAhead: z.number().optional(),
  /** The provider/location combinations the EHR returned this reason for. */
  offeredAt: z.array(ehrProviderLocationSchema),
});

export type EhrVisitReason = z.infer<typeof ehrVisitReasonSchema>;
